import { useCallback, useEffect, useRef, useState } from 'react';
import { listQueuedSignatures, subscribeOutbox, updateQueuedSignature } from '@/lib/offline/outbox';
import { syncOutbox } from '@/lib/offline/sync-signatures';
import { useOfflineStatus } from '@/hooks/use-offline';
import type { QueuedSignature } from '@/lib/offline/outbox';
import type { SyncResult } from '@/lib/offline/sync-signatures';

export interface UseSignatureOutboxOptions {
  onSync?: (result: SyncResult) => void;
}

export function useSignatureOutbox({ onSync }: UseSignatureOutboxOptions = {}) {
  const { isOffline } = useOfflineStatus();
  const [queuedCount, setQueuedCount] = useState(0);
  const [items, setItems] = useState<QueuedSignature[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [conflictCount, setConflictCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const mountedRef = useRef(true);
  const syncingRef = useRef(false);
  const initialAutoSyncRef = useRef(false);
  const wasOfflineRef = useRef(isOffline);

  const refresh = useCallback(async () => {
    try {
      const records = await listQueuedSignatures();
      if (!mountedRef.current) {
        return;
      }
      const pending = records.filter((record) => record.status === 'pending').length;
      const failed = records.filter((record) => record.status === 'failed').length;
      const conflict = records.filter((record) => record.status === 'conflict').length;
      setItems(records);
      setQueuedCount(records.length);
      setPendingCount(pending);
      setFailedCount(failed);
      setConflictCount(conflict);
    } catch {
      if (mountedRef.current) {
        setItems([]);
        setQueuedCount(0);
        setPendingCount(0);
        setFailedCount(0);
        setConflictCount(0);
      }
    }
  }, []);

  const syncNow = useCallback(async () => {
    if (syncingRef.current) {
      return;
    }

    syncingRef.current = true;
    if (mountedRef.current) {
      setIsSyncing(true);
    }

    try {
      const result = await syncOutbox();
      onSync?.(result);
    } finally {
      syncingRef.current = false;
      if (mountedRef.current) {
        setIsSyncing(false);
      }
      await refresh();
    }
  }, [refresh, onSync]);

  const retryFailed = useCallback(async () => {
    const failedItems = items.filter((item) => item.status === 'failed');
    await Promise.all(failedItems.map((item) => updateQueuedSignature(item.id, { status: 'pending', attempts: 0 })));
    await syncNow();
  }, [items, syncNow]);

  useEffect(() => {
    mountedRef.current = true;
    const unsubscribe = subscribeOutbox(refresh);
    void refresh();
    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, [refresh]);

  useEffect(() => {
    if (wasOfflineRef.current && !isOffline) {
      initialAutoSyncRef.current = true;
      void syncNow();
    }
    wasOfflineRef.current = isOffline;
  }, [isOffline, syncNow]);

  useEffect(() => {
    if (initialAutoSyncRef.current) {
      return;
    }
    if (isOffline || items.length === 0) {
      return;
    }
    initialAutoSyncRef.current = true;
    void syncNow();
  }, [isOffline, items.length, syncNow]);

  return {
    isOffline,
    queuedCount,
    refresh,
    items,
    pendingCount,
    failedCount,
    conflictCount,
    isSyncing,
    syncNow,
    retryFailed,
  };
}
